import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AboutSubnav, AboutHero } from "../../components/AboutLayout";
import Reveal from "../../components/Reveal";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Masonry-style varied tall/short tiles
const TILE_PATTERN = ["tall", "short", "short", "tall", "short", "tall", "short", "short"];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/gallery`)
      .then((r) => {
        if (mounted && Array.isArray(r.data)) setItems(r.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const tiles = useMemo(
    () =>
      items.map((it, i) => ({
        ...it,
        idx: i,
        size: TILE_PATTERN[i % TILE_PATTERN.length],
      })),
    [items]
  );

  const close = () => setActiveIdx(null);
  const prev = (e) => {
    e?.stopPropagation();
    setActiveIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));
  };
  const next = (e) => {
    e?.stopPropagation();
    setActiveIdx((i) => (i === null ? null : (i + 1) % items.length));
  };

  return (
    <div data-testid="about-gallery" className="grain bg-[#f7f7f5]">
      <AboutSubnav />
      <AboutHero
        kicker={`/ Gallery · ${items.length} images`}
        title="Yards, dispatch,"
        accent=" real work."
        subtitle="A visual walkthrough of our branches, weighbridges, fleet, and the day-to-day at Baheti Steel Traders."
      />

      <section className="bg-white py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div
            data-testid="gallery-masonry"
            className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4"
          >
            {tiles.map((t) => (
              <Reveal
                key={t.id || `${t.url}-${t.idx}`}
                delay={(t.idx % 6) * 60}
                className="break-inside-avoid"
              >
                <button
                  onClick={() => setActiveIdx(t.idx)}
                  data-testid={`gallery-tile-${t.idx}`}
                  className="group relative block w-full overflow-hidden rounded-2xl bg-[#efefe9] shadow-lux transition-all duration-500 hover:-translate-y-1 hover:shadow-lux-hover"
                >
                  <img
                    src={t.url}
                    alt={`Baheti Steel Traders — gallery ${t.idx + 1}`}
                    loading="lazy"
                    className={`w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110 ${
                      t.size === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                  <div className="absolute bottom-4 left-4 rounded-full bg-white/90 px-3 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#1c1c1e] opacity-0 transition-all duration-300 group-hover:opacity-100">
                    {String(t.idx + 1).padStart(2, "0")} · View
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {activeIdx !== null && items[activeIdx] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1c1c1e]/95 p-4 backdrop-blur md:p-8"
          onClick={close}
        >
          <button
            onClick={close}
            data-testid="lightbox-close"
            className="absolute right-4 top-4 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-[#1c1c1e] transition-all hover:scale-110"
          >
            <X size={18} />
          </button>
          <button
            onClick={prev}
            data-testid="lightbox-prev"
            className="absolute left-4 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1c1c1e] transition-all hover:scale-110 md:left-8"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={next}
            data-testid="lightbox-next"
            className="absolute right-4 top-1/2 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[#1c1c1e] transition-all hover:scale-110 md:right-8"
          >
            <ChevronRight size={20} />
          </button>
          <img
            src={items[activeIdx].url}
            alt={`Baheti Steel Traders — gallery ${activeIdx + 1}`}
            className="max-h-[85vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/90 px-4 py-1.5 font-mono-industrial text-[10px] uppercase tracking-[0.25em] text-[#1c1c1e]">
            {String(activeIdx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </div>
        </div>
      )}
    </div>
  );
}
